/**
 * Pick Your Favorites
 * Sidebar Controller
 *
 * Responsibilities:
 * - update progress text and progress bar
 * - render selected candy slots
 * - update subtotal, discount, and total
 * - enable the cart button only when the bag is full
 * - display toast messages
 */

window.PYF = window.PYF || {};


/* =====================================================
   GET SIDEBAR
===================================================== */

PYF.getSidebar = function() {
  return document.querySelector(
    ".pyf-sidebar"
  );
};


/* =====================================================
   MONEY FORMAT
===================================================== */

PYF.formatMoney = function(amount) {
  const safeAmount =
    Number(amount) || 0;

  return `$${safeAmount.toFixed(2)}`;
};


/* =====================================================
   TOAST
===================================================== */

PYF.showToast = function(
  title,
  message
) {
  const toast =
    document.querySelector(
      ".pyf-toast"
    );

  if (!toast) {
    console.warn(
      title,
      message
    );

    return;
  }

  const titleElement =
    toast.querySelector(
      "strong"
    );

  const messageElement =
    toast.querySelector(
      "p"
    );

  if (titleElement) {
    titleElement.textContent =
      title || "";
  }

  if (messageElement) {
    messageElement.textContent =
      message || "";
  }

  toast.classList.add(
    "show"
  );

  window.clearTimeout(
    PYF.toastTimeout
  );

  PYF.toastTimeout =
    window.setTimeout(
      function() {
        toast.classList.remove(
          "show"
        );
      },
      3200
    );
};


/* =====================================================
   GET SIDEBAR ELEMENTS
===================================================== */

PYF.getSidebarElements = function() {
  const sidebar =
    PYF.getSidebar();

  if (!sidebar) {
    return null;
  }

  return {
    sidebar: sidebar,

    progressText:
      sidebar.querySelector(
        "#pyf-progress-text"
      ) ||
      sidebar.querySelector(
        ".pyf-progress-text"
      ),

    progressFill:
      sidebar.querySelector(
        "#pyf-progress-fill"
      ) ||
      sidebar.querySelector(
        ".pyf-progress-fill"
      ),

    progressBar:
      sidebar.querySelector(
        ".pyf-progress-bar"
      ),

    placeholders:
      Array.from(
        sidebar.querySelectorAll(
          ".pyf-placeholder"
        )
      ),

    subtotal:
      sidebar.querySelector(
        "#pyf-subtotal"
      ) ||
      sidebar.querySelector(
        ".pyf-subtotal-value"
      ),

    discount:
      sidebar.querySelector(
        "#pyf-discount"
      ) ||
      sidebar.querySelector(
        ".pyf-discount-value"
      ),

    total:
      sidebar.querySelector(
        "#pyf-total"
      ) ||
      sidebar.querySelector(
        ".pyf-total-value"
      ),

    cartButton:
      sidebar.querySelector(
        "#pyf-add-bag"
      ) ||
      sidebar.querySelector(
        ".pyf-add-bag-button"
      ) ||
      sidebar.querySelector(
        ".pyf-checkout-button"
      )
  };
};


/* =====================================================
   UPDATE PROGRESS
===================================================== */

PYF.updateProgress = function(
  elements
) {
  if (!elements) {
    return;
  }

  const currentPicks =
    Number(
      PYF.state.currentPicks
    ) || 0;

  const maxPicks =
    Number(
      PYF.state.bag.maxPicks
    ) || 0;

  const percentage =
    PYF.getProgressPercentage();

  if (elements.progressText) {
    elements.progressText.textContent =
      `${currentPicks} / ${maxPicks} Picks`;
  }

  if (elements.progressFill) {
    elements.progressFill.style.width =
      `${percentage}%`;

    elements.progressFill.setAttribute(
      "aria-hidden",
      "true"
    );
  }

  if (elements.progressBar) {
    elements.progressBar.setAttribute(
      "role",
      "progressbar"
    );

    elements.progressBar.setAttribute(
      "aria-valuemin",
      "0"
    );

    elements.progressBar.setAttribute(
      "aria-valuemax",
      String(maxPicks)
    );

    elements.progressBar.setAttribute(
      "aria-valuenow",
      String(currentPicks)
    );

    elements.progressBar.setAttribute(
      "aria-label",
      `${currentPicks} of ${maxPicks} picks selected`
    );
  }
};


/* =====================================================
   RESET ONE SLOT
===================================================== */

PYF.resetSlot = function(slot) {
  if (!slot) {
    return;
  }

  slot.classList.remove(
    "active"
  );

  slot.removeAttribute(
    "data-product-id"
  );

  slot.style.display =
    "none";

  const image =
    slot.querySelector(
      ".pyf-slot-image"
    );

  const count =
    slot.querySelector(
      ".pyf-slot-count"
    );

  const plus =
    slot.querySelector(
      ".pyf-slot-plus"
    );

  if (image) {
    image.src = "";
    image.alt = "";
    image.style.display =
      "none";
  }

  if (count) {
    count.textContent = "";
    count.style.display =
      "none";
  }

  if (plus) {
    plus.style.display =
      "";
  }
};


/* =====================================================
   RENDER EMPTY SLOT
===================================================== */

PYF.renderEmptySlot = function(
  slot
) {
  if (!slot) {
    return;
  }

  slot.classList.remove(
    "active"
  );

  slot.removeAttribute(
    "data-product-id"
  );

  slot.style.display =
    "";

  const image =
    slot.querySelector(
      ".pyf-slot-image"
    );

  const count =
    slot.querySelector(
      ".pyf-slot-count"
    );

  const plus =
    slot.querySelector(
      ".pyf-slot-plus"
    );

  if (image) {
    image.src = "";
    image.alt = "";
    image.style.display =
      "none";
  }

  if (count) {
    count.textContent = "";
    count.style.display =
      "none";
  }

  if (plus) {
    plus.style.display =
      "";
  }
};


/* =====================================================
   RENDER ACTIVE SLOT
===================================================== */

PYF.renderActiveSlot = function(
  slot,
  item
) {
  if (!slot || !item) {
    return;
  }

  slot.classList.add(
    "active"
  );

  slot.style.display = "";

  slot.dataset.productId =
    String(item.productId);

  const image =
    slot.querySelector(
      ".pyf-slot-image"
    );

  const count =
    slot.querySelector(
      ".pyf-slot-count"
    );

  const plus =
    slot.querySelector(
      ".pyf-slot-plus"
    );

  if (plus) {
    plus.style.display =
      "none";
  }

  if (image) {
    if (item.image) {
      image.src =
        item.image;

      image.alt =
        item.title || "Selected candy";

      image.style.display =
        "block";
    } else {
      image.src = "";
      image.alt = "";
      image.style.display =
        "none";
    }
  }

  if (count) {
    count.textContent =
      `×${item.quantity}`;

    count.style.display =
      "flex";
  }
};


/* =====================================================
   UPDATE SLOTS
===================================================== */

PYF.updateSlots = function(
  elements
) {
  if (!elements) {
    return;
  }

  const slots =
    elements.placeholders;

  const selectedItems =
    PYF.getSelectedItems();

  const maxPicks =
    Number(
      PYF.state.bag.maxPicks
    ) || 0;

  /*
   * First completely reset all available slots.
   */
  slots.forEach(function(slot) {
    PYF.resetSlot(slot);
  });

  /*
   * One active slot represents one unique candy.
   * Its quantity appears below the candy image.
   */
  selectedItems.forEach(
    function(item, index) {
      if (!slots[index]) {
        return;
      }

      PYF.renderActiveSlot(
        slots[index],
        item
      );
    }
  );

  /*
   * The remaining visible slots represent
   * remaining available picks.
   *
   * Example:
   * 8-pick bag
   * Candy A × 3
   * Candy B × 2
   *
   * Visible slots:
   * 2 active unique-candy slots
   * 3 empty remaining-pick slots
   */
  const remainingPicks =
    PYF.getRemainingPicks();

  const visibleSlotCount =
    Math.min(
      slots.length,
      selectedItems.length +
        remainingPicks
    );

  for (
    let index =
      selectedItems.length;
    index < visibleSlotCount;
    index += 1
  ) {
    PYF.renderEmptySlot(
      slots[index]
    );
  }

  /*
   * Safety:
   * Never show more circles than the selected
   * bag can reasonably need.
   */
  const maximumVisible =
    Math.min(
      slots.length,
      Math.max(
        selectedItems.length,
        maxPicks
      )
    );

  for (
    let index =
      visibleSlotCount;
    index < maximumVisible;
    index += 1
  ) {
    slots[index].style.display =
      "none";
  }
};


/* =====================================================
   UPDATE TOTALS
===================================================== */

PYF.updateTotals = function(elements) {
  if (!elements) {
    return;
  }


  const subtotal =
    typeof PYF.getSubtotal === "function"
      ? Number(PYF.getSubtotal()) || 0
      : (
          Number(PYF.state?.currentPicks) || 0
        ) * 1.89;


  const discount =
    typeof PYF.getDiscount === "function"
      ? Number(PYF.getDiscount()) || 0
      : 0;


  const total =
    typeof PYF.getTotal === "function"
      ? Number(PYF.getTotal()) || 0
      : Math.max(
          0,
          subtotal - discount
        );


  if (elements.subtotal) {
    elements.subtotal.textContent =
      PYF.formatMoney(subtotal);
  }


 if (elements.discount) {

  elements.discount.textContent =
    discount > 0
      ? `-${PYF.formatMoney(discount)}`
      : PYF.formatMoney(0);

}


  if (elements.total) {
    elements.total.textContent =
      PYF.formatMoney(total);
  }
};

/* =====================================================
   UPDATE CART BUTTON
===================================================== */

PYF.updateCartButton = function(
  elements
) {
  if (
    !elements ||
    !elements.cartButton
  ) {
    return;
  }

  const isFull =
    PYF.isBagFull();

  const currentPicks =
    PYF.state.currentPicks;

  const maxPicks =
    PYF.state.bag.maxPicks;

  elements.cartButton.disabled =
    !isFull;

  elements.cartButton.setAttribute(
    "aria-disabled",
    isFull
      ? "false"
      : "true"
  );

  elements.cartButton.dataset.bagSize =
    PYF.state.bag.size;

  elements.cartButton.dataset.maxPicks =
    String(maxPicks);

  if (!elements.cartButton.dataset.defaultText) {
    elements.cartButton.dataset.defaultText =
      elements.cartButton.textContent.trim() ||
      "Add Bag to Cart";
  }

 if (isFull) {

    elements.cartButton.textContent =
    PYF.editingBag
      ? "Update Bag"
      : "Add Bag to Cart";

    } else {
    const remaining =
      Math.max(
        0,
        maxPicks - currentPicks
      );

    elements.cartButton.textContent =
      remaining === 1
        ? "Add 1 More Pick"
        : `Add ${remaining} More Picks`;
  }
};


/* =====================================================
   UPDATE COMPLETE SIDEBAR
===================================================== */

PYF.updateSidebar = function() {
  if (!PYF.state) {
    return;
  }

  const elements =
    PYF.getSidebarElements();

  if (!elements) {
    return;
  }

  PYF.updateProgress(
    elements
  );

  PYF.updateSlots(
    elements
  );

  PYF.updateTotals(
    elements
  );

  PYF.updateCartButton(
    elements
  );
};


/* =====================================================
   INITIALIZE SIDEBAR
===================================================== */

PYF.initSidebar = function() {
  const sidebar =
    PYF.getSidebar();

  if (!sidebar) {
    console.warn(
      "PYF.initSidebar: Sidebar not found."
    );

    return;
  }

  PYF.updateSidebar();
};


/* =====================================================
   STATE CHANGE LISTENER
===================================================== */

document.addEventListener(
  "pyf:state-changed",
  function(event) {
    PYF.updateSidebar();

    if (
      event.detail &&
      event.detail.reason ===
        "bag-full"
    ) {
      PYF.showToast(
        "Bag is full",
        "Remove a pick or add this bag to the cart before selecting more candy."
      );
    }
  }
);